import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, Snackbar } from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { getUnlockContext, unlockDoor } from "../api/smartLockApi";

export default function SmartLockUnlockButton({
    affiliateId,
    classId,
    disabled = false,
    fullWidthOnMobile = false,
    fullWidth = false,
    eligibleOnly = false,
    sx = {}
}) {
    const [context, setContext] = useState(null);
    const [selectedDoorId, setSelectedDoorId] = useState("");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState({ open: false, text: "", severity: "success" });

    useEffect(() => {
        let mounted = true;
        const run = async () => {
            if (!affiliateId) return;
            try {
                const response = await getUnlockContext(affiliateId, {
                    classId,
                    eligibleOnly,
                });
                if (!mounted) return;
                setContext(response);
                const firstDoorId = response?.doors?.[0]?.id;
                setSelectedDoorId(firstDoorId || "");
            } catch (error) {
                if (!mounted) return;
                setContext({ success: false, isSmartLockEnabled: false, doors: [] });
            }
        };
        run();
        return () => {
            mounted = false;
        };
    }, [affiliateId, classId, eligibleOnly]);

    const canShowUnlock = useMemo(() => {
        return Boolean(context?.isSmartLockEnabled && Array.isArray(context?.doors) && context.doors.length > 0);
    }, [context]);

    const handleUnlock = async () => {
        if (!selectedDoorId) return;
        setBusy(true);
        try {
            const response = await unlockDoor({
                affiliateId,
                doorId: selectedDoorId,
                classId,
            });
            setMessage({
                open: true,
                text: response?.message || "Unlock command sent.",
                severity: "success",
            });
        } catch (error) {
            setMessage({
                open: true,
                text: error?.response?.data?.message || "Unable to unlock door.",
                severity: "error",
            });
        } finally {
            setBusy(false);
        }
    };

    if (!canShowUnlock) {
        return null;
    }

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                ...(fullWidthOnMobile
                    ? {
                        flexDirection: { xs: "column", sm: "row" },
                        width: { xs: "100%", sm: "auto" },
                    }
                    : {}),
                ...(fullWidth
                    ? {
                        width: "100%",
                        justifyContent: "space-between",
                    }
                    : {}),
                ...sx,
            }}
        >
            <FormControl
                size="small"
                sx={{
                    minWidth: 170,
                    ...(fullWidthOnMobile ? { width: { xs: "100%", sm: "170px" } } : {}),
                    ...(fullWidth ? { flex: 1, minWidth: 0, mr: 1 } : {}),
                }}
            >
                <InputLabel>Door</InputLabel>
                <Select
                    value={selectedDoorId}
                    label="Door"
                    onChange={(event) => setSelectedDoorId(event.target.value)}
                    disabled={busy || disabled}
                >
                    {context.doors.map((door) => (
                        <MenuItem key={door.id} value={door.id}>
                            {door.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Button
                variant="contained"
                color="success"
                startIcon={<LockOpenIcon />}
                onClick={handleUnlock}
                disabled={busy || disabled || !selectedDoorId}
                sx={{
                    ...(fullWidthOnMobile ? { width: { xs: "100%", sm: "auto" } } : {}),
                    ...(fullWidth ? { flexShrink: 0 } : {}),
                }}
            >
                Open Door
            </Button>
            <Snackbar
                open={message.open}
                autoHideDuration={3500}
                onClose={() => setMessage((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    severity={message.severity}
                    variant="filled"
                    onClose={() => setMessage((prev) => ({ ...prev, open: false }))}
                >
                    {message.text}
                </Alert>
            </Snackbar>
        </Box>
    );
}
